import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Save, Trophy, Gamepad2, Play, Rocket, Star, Coins, Users, Crown, Layers, ExternalLink, Link2, Clock, Image as ImageIcon, Share2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, Spinner } from "@/components/Bits";
import RacingCareerCard from "@/components/RacingCareerCard";
import AchievementCard from "@/components/AchievementCard";
import { BANNER_PRESETS, bannerClass } from "@/lib/banners";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const EMOJIS = ["🎮", "🐱", "🐶", "🤖", "🦄", "🐉", "🚀", "👾", "🦊", "🐸", "🌟", "🦋"];
const SOCIAL_FIELDS = [
  ["website", "Website", "https://mygames.com"],
  ["youtube", "YouTube", "https://youtube.com/@you"],
  ["twitter", "Twitter / X", "https://x.com/you"],
  ["discord", "Discord", "https://discord.gg/your-server"],
  ["twitch", "Twitch", "https://twitch.tv/you"],
  ["patreon", "Patreon / Donations", "https://patreon.com/you"],
];

export default function Profile() {
  const { user, patchUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", bio: "", avatar_emoji: "🎮", banner: "aurora", social_links: {} });
  const [shareAch, setShareAch] = useState(null);

  useEffect(() => {
    api.get("/me/profile").then((r) => {
      setData(r.data);
      setForm({ name: r.data.user.name, bio: r.data.user.bio || "", avatar_emoji: r.data.user.avatar_emoji || "🎮", banner: r.data.user.banner || "aurora", social_links: r.data.user.social_links || {} });
    });
  }, []);
  if (!data) return <Spinner />;

  const save = async () => {
    const { data: u } = await api.patch("/me/profile", form);
    patchUser(u);
    toast.success("Profile updated!");
  };
  const setLink = (k, v) => setForm((f) => ({ ...f, social_links: { ...f.social_links, [k]: v } }));

  const s = data.stats;
  const pct = s.xp_to_next ? Math.round((s.xp_into_level / s.xp_to_next) * 100) : 0;

  return (
    <div className="p-5 lg:p-8 max-w-4xl mx-auto" data-testid="profile-page">
      <PageHeader title="My Profile" icon={User} action={
        <Button variant="outline" onClick={() => navigate(`/app/creators/${user.id}`)} className="rounded-full" data-testid="view-public-profile">
          <ExternalLink className="w-4 h-4" /> View public profile
        </Button>
      } />

      {/* Identity + XP */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden mb-6">
        <div className={`h-20 ${bannerClass(form.banner)}`} data-testid="profile-banner" />
        <div className="p-6 -mt-10">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-20 h-20 rounded-full grid place-items-center text-4xl bg-gradient-to-br from-[#7c3aed] to-[#3b9eff] shrink-0 border-4 border-[hsl(var(--card))]">{form.avatar_emoji}</div>
            <div className="min-w-0 flex-1 pt-8">
              <div className="font-display text-2xl font-bold truncate flex items-center gap-2">
                {data.user.name}
                {data.tips_given?.count > 0 && (
                  <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white gap-1" data-testid="supporter-flair">💚 Supporter</Badge>
                )}
                {(data.user.dare_streak ?? user?.dare_streak) > 0 && (
                  <Badge className="bg-orange-500 hover:bg-orange-500 text-white gap-1" data-testid="dare-streak-badge">🔥 {data.user.dare_streak ?? user?.dare_streak}-dare streak</Badge>
                )}
                {(data.user.taunt_of_week_wins ?? 0) > 0 && (
                  <Badge className="bg-[#f5b301] hover:bg-[#f5b301] text-black gap-1" data-testid="taunt-crown-badge">👑 Taunt of the Week{data.user.taunt_of_week_wins > 1 ? ` ×${data.user.taunt_of_week_wins}` : ""}</Badge>
                )}
                {data.certified_creator && (
                  <Badge className="bg-violet-600 hover:bg-violet-600 text-white gap-1" data-testid="certified-creator-badge">🤖 Certified Creator{data.certified_games > 1 ? ` ×${data.certified_games}` : ""}</Badge>
                )}
                {(data.user.coop_stats?.medic) && (
                  <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white gap-1" data-testid="medic-badge">🩹 Medic</Badge>
                )}
              </div>
              <div className="text-sm text-[hsl(var(--muted-foreground))] capitalize">{data.user.role} · Level {s.level}</div>
              {data.user.bio && <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 line-clamp-2">{data.user.bio}</p>}
            </div>
          </div>
          {data.tips_given?.count > 0 && (
            <div className="mb-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-[hsl(var(--foreground))]" data-testid="supporter-note">
              💚 You've backed <b>{data.tips_given.creators}</b> creator{data.tips_given.creators === 1 ? "" : "s"} with <b>{data.tips_given.count}</b> tip{data.tips_given.count === 1 ? "" : "s"} (${data.tips_given.total.toFixed(2)}) — thank you! You earn <b>bonus AI credits</b> on every tip.
            </div>
          )}
          <div className="flex items-center justify-between text-xs text-[hsl(var(--muted-foreground))] mb-1.5">
            <span className="font-semibold text-[hsl(var(--foreground))]">GameView XP — Level {s.level}</span>
            <span className="tabular-nums">{s.xp_into_level} / {s.xp_to_next} → Lv {s.level + 1}</span>
          </div>
          <Progress value={pct} className="h-3" data-testid="profile-xp-progress" />
        </div>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3" data-testid="profile-stats">
        <StatCard label="Created" value={s.games_created} icon={Gamepad2} delay={1} />
        <StatCard label="Published" value={s.games_published} icon={Rocket} accent="#10b981" delay={2} />
        <StatCard label="Total plays" value={s.total_plays} icon={Play} accent="#f59e0b" delay={3} />
        <StatCard label="Hours played" value={s.hours_played ?? 0} icon={Clock} accent="#3b9eff" delay={4} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="Levels cleared" value={s.levels_completed} icon={Layers} accent="#06b6d4" delay={1} />
        <StatCard label="Stars" value={s.stars} icon={Star} accent="#eab308" delay={2} />
        <StatCard label="Collaborations" value={s.collaborations ?? 0} icon={Users} accent="#ec4899" delay={3} />
        <StatCard label="Packs owned" value={s.owned_packs} icon={Crown} accent="#a855f7" delay={4} />
      </div>

      {/* Recent achievements */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mb-6" data-testid="profile-recent-achievements">
        <div className="font-display font-bold mb-3 flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Recent achievements</div>
        {data.recent_achievements?.length ? (
          <div className="flex flex-wrap gap-2">
            {data.recent_achievements.map((a, i) => (
              <div key={i} className="flex items-center gap-2 rounded-full border border-[hsl(var(--border))] pl-2 pr-3 py-1.5 text-sm">
                <span className="text-lg">{a.icon}</span> <span className="font-semibold">{a.title}</span>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-[hsl(var(--muted-foreground))]">No badges yet — create, publish and play games to earn XP and trophies!</p>}
      </div>

      <RacingCareerCard />

      {data.user.coop_stats && (data.user.coop_stats.rescues || data.user.coop_stats.kills || data.user.coop_stats.boss_kills || data.user.coop_stats.medic) ? (
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 my-6" data-testid="profile-coop-stats">
          <div className="font-display font-bold mb-3 flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-400" /> Co-op record
            {data.user.coop_stats.medic && <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white gap-1 ml-1">🩹 Medic</Badge>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Teammates revived" value={data.user.coop_stats.rescues || 0} icon={Users} accent="#10b981" delay={1} />
            <StatCard label="Co-op kills" value={data.user.coop_stats.kills || 0} icon={Trophy} accent="#f59e0b" delay={2} />
            <StatCard label="Bosses felled" value={data.user.coop_stats.boss_kills || 0} icon={Crown} accent="#a855f7" delay={3} />
            <StatCard label="Best loot roll" value={data.user.coop_stats.best_roll || 0} icon={Star} accent="#3b9eff" delay={4} />
          </div>
        </div>
      ) : null}

      {data.user.coop_stats && (data.user.coop_stats.rescues || data.user.coop_stats.kills || data.user.coop_stats.boss_kills || data.user.coop_stats.medic) ? (
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 my-6" data-testid="profile-coop-achievements">
          <div className="font-display font-bold mb-3 flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-400" /> Co-op achievements</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {(() => {
              const cs = data.user.coop_stats || {};
              const ua = cs.unlocked_at || {};
              const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return null; } };
              const list = [
                { key: "first_responder", icon: "🩹", name: "First Responder", cur: cs.rescues || 0, goal: 1 },
                { key: "field_medic", icon: "⛑️", name: "Field Medic", cur: cs.rescues || 0, goal: 10 },
                { key: "guardian_angel", icon: "😇", name: "Guardian Angel", cur: cs.rescues || 0, goal: 50 },
                { key: "monster_hunter", icon: "💥", name: "Monster Hunter", cur: cs.kills || 0, goal: 50 },
                { key: "slayer", icon: "⚔️", name: "Slayer", cur: cs.kills || 0, goal: 250 },
                { key: "boss_breaker", icon: "👑", name: "Boss Breaker", cur: cs.boss_kills || 0, goal: 5 },
                { key: "raid_boss", icon: "🐉", name: "Raid Boss", cur: cs.boss_kills || 0, goal: 25 },
                { key: "squad_mvp", icon: "🏆", name: "Squad MVP", cur: cs.mvp || 0, goal: 1 },
                { key: "lucky_roller", icon: "🎲", name: "Lucky Roller", cur: cs.best_roll || 0, goal: 100 },
              ];
              return list.map((a) => {
                const have = a.cur >= a.goal;
                const pct = Math.min(100, Math.round((a.cur / a.goal) * 100));
                const when = have && ua[a.key] ? fmt(ua[a.key]) : null;
                return (
                  <div key={a.name} className={`rounded-xl border p-2.5 flex items-center gap-2.5 transition ${have ? "border-emerald-500/50 bg-emerald-500/10" : "border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30"}`} data-testid={`coop-ach-${a.name.replace(/\s+/g, "-").toLowerCase()}`} title={`${a.cur} / ${a.goal}`}>
                    <span className={`text-2xl ${have ? "" : "grayscale opacity-40"}`}>{a.icon}</span>
                    <div className="min-w-0 flex-1">
                      <div className={`text-xs font-bold truncate ${have ? "" : "text-[hsl(var(--muted-foreground))]"}`}>{a.name}</div>
                      {have ? (
                        <div className="text-[10px] font-semibold text-emerald-500">Unlocked ✓{when ? ` · ${when}` : ""}</div>
                      ) : (
                        <div className="mt-1 h-1.5 rounded-full bg-[hsl(var(--muted))] overflow-hidden"><div className="h-full bg-[hsl(var(--primary))]" style={{ width: `${pct}%` }} /></div>
                      )}
                    </div>
                    {have && (
                      <button onClick={() => setShareAch({ icon: a.icon, name: a.name })} className="shrink-0 p-1.5 rounded-full hover:bg-[hsl(var(--accent))] text-[hsl(var(--muted-foreground))] hover:text-emerald-500 transition-colors" title="Share this badge" data-testid={`coop-ach-share-${a.key}`}>
                        <Share2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      ) : null}

      {/* Editable profile */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
        <div className="font-display font-bold mb-4">Edit profile</div>
        <div className="space-y-4">
          <div>
            <Label className="mb-2 block">Avatar</Label>
            <div className="flex flex-wrap gap-2">
              {EMOJIS.map((e) => (
                <button key={e} onClick={() => setForm((f) => ({ ...f, avatar_emoji: e }))} data-testid={`avatar-${e}`}
                  className={`w-11 h-11 rounded-xl text-xl grid place-items-center border-2 transition-all ${form.avatar_emoji === e ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15" : "border-[hsl(var(--border))]"}`}>{e}</button>
              ))}
            </div>
          </div>
          <div><Label htmlFor="name">Name</Label><Input id="name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5" data-testid="profile-name" /></div>
          <div>
            <Label className="mb-2 flex items-center gap-1.5"><ImageIcon className="w-4 h-4" /> Profile banner</Label>
            <div className="flex flex-wrap gap-2" data-testid="profile-banner-picker">
              {BANNER_PRESETS.map((b) => (
                <button key={b.key} onClick={() => setForm((f) => ({ ...f, banner: b.key }))} title={b.label} data-testid={`banner-${b.key}`}
                  className={`h-9 w-16 rounded-lg ${b.className} border-2 transition-all ${form.banner === b.key ? "border-[hsl(var(--primary))] ring-2 ring-[hsl(var(--primary))]/40 scale-105" : "border-transparent opacity-80 hover:opacity-100"}`} />
              ))}
            </div>
          </div>
          <div><Label htmlFor="bio">Bio</Label><Textarea id="bio" value={form.bio} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))} placeholder="Tell the world about your games…" className="mt-1.5" data-testid="profile-bio" /></div>
          <div>
            <Label className="mb-2 flex items-center gap-1.5"><Link2 className="w-4 h-4" /> Social & support links</Label>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {SOCIAL_FIELDS.map(([key, label, ph]) => (
                <div key={key}>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">{label}</span>
                  <Input value={form.social_links[key] || ""} onChange={(e) => setLink(key, e.target.value)} placeholder={ph} className="mt-1" data-testid={`profile-social-${key}`} />
                </div>
              ))}
            </div>
          </div>
          <Button onClick={save} data-testid="profile-save" className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]"><Save className="w-4 h-4" /> Save changes</Button>
        </div>
      </div>

      <AchievementCard achievement={shareAch} user={data.user} open={!!shareAch} onOpenChange={(v) => { if (!v) setShareAch(null); }} />
    </div>
  );
}
